import React, { useState, useEffect } from "react";
import { Modal, Button, Checkbox, Placeholder, Input } from "rsuite";
import * as productAttributeManagement from "../../../services/productAttributeManagement";
import * as productPreSubManagement from "../../../services/productPreSubManagement";
import Swal from "sweetalert2";
import { SWAL_SETTINGS } from "../../../../../utils/Constants";

const AssignAttributesModal = ({ open, onClose, preSubCategory, onSuccess }) => {
    const [attributes, setAttributes] = useState([]);
    const [selectedAttributes, setSelectedAttributes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [groupings, setGroupings] = useState([]);

    useEffect(() => {
        const loadInitialData = async () => {
            setLoading(true);
            try {
                const productManagement = await import("../../../services/productManagement");
                const [grpRes, attrRes] = await Promise.all([
                    productManagement.GroupingList({ page: 1, per_page: 1000 }),
                    productAttributeManagement.List({ page: 1, per_page: 1000, sort: { column: "name", order: 1 } })
                ]);
                
                if (grpRes.success) {
                    setGroupings(grpRes.data.list || []);
                }
                
                if (attrRes.success) {
                    setAttributes(attrRes.data.list || []);
                }
            } catch (error) {
                console.error("Error fetching data:", error);
            }
            setLoading(false);
        };

        if (open) {
            loadInitialData();
        }
    }, [open]);

    // Initial load of selected attributes
    useEffect(() => {
        if (open && preSubCategory) {
            if (preSubCategory.attributes) {
                setSelectedAttributes(preSubCategory.attributes);
            } else {
                productPreSubManagement.Details(preSubCategory._id).then(res => {
                    if (res.success && res.data.attributes) {
                        setSelectedAttributes(res.data.attributes);
                    }
                });
            }
        } else {
            setSelectedAttributes([]);
            setAttributes([]);
            setGroupings([]);
            setSearchQuery("");
        }
    }, [open, preSubCategory]);

    const handleCheckboxChange = (id, checked) => {
        if (checked) {
            setSelectedAttributes([...selectedAttributes, id]);
        } else {
            setSelectedAttributes(selectedAttributes.filter(attrId => attrId !== id));
        }
    };

    const handleGroupCheckboxChange = (groupId, checked) => {
        const groupAttrs = attributes.filter(a => {
            const attrGroupId = a.groupingId?._id || a.groupingId;
            return attrGroupId === groupId;
        }).map(a => a._id);

        if (checked) {
            setSelectedAttributes(prev => Array.from(new Set([...prev, ...groupAttrs])));
        } else {
            setSelectedAttributes(prev => prev.filter(id => !groupAttrs.includes(id)));
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const response = await productPreSubManagement.AssignAttributes({
                _id: preSubCategory._id,
                attributes: selectedAttributes
            });
            if (response.success) {
                Swal.fire({
                    icon: "success",
                    text: "Attributes assigned successfully",
                    ...SWAL_SETTINGS
                });
                onSuccess();
                onClose();
            } else {
                Swal.fire({
                    icon: "error",
                    text: response.message || "Failed to assign attributes",
                    ...SWAL_SETTINGS
                });
            }
        } catch (error) {
            Swal.fire({
                icon: "error",
                text: "An error occurred while saving",
                ...SWAL_SETTINGS
            });
        }
        setSaving(false);
    };

    const filteredAttributes = attributes.filter(attr => 
        attr.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const renderGroupings = () => {
        if (loading) {
            return <Placeholder.Paragraph rows={5} active />;
        }

        const groupsToRender = groupings.map(group => {
            const groupAttrs = filteredAttributes.filter(a => {
                const attrGroupId = a.groupingId?._id || a.groupingId;
                return attrGroupId === group._id;
            });

            if (groupAttrs.length === 0) return null;

            const allSelected = groupAttrs.every(a => selectedAttributes.includes(a._id));
            const someSelected = groupAttrs.some(a => selectedAttributes.includes(a._id));

            return (
                <div key={group._id} className="mb-4 border rounded p-3">
                    <div className="d-flex align-items-center mb-3">
                        <Checkbox 
                            checked={allSelected} 
                            indeterminate={someSelected && !allSelected}
                            onChange={(value, checked) => handleGroupCheckboxChange(group._id, checked)}
                        >
                            <h5 className="m-0 font-weight-bold">{group.title}</h5>
                        </Checkbox>
                    </div>
                    <div className="row m-0">
                        {groupAttrs.map(attr => (
                            <div key={attr._id} className="col-md-6 mb-2">
                                <div className="d-flex align-items-center gap-2 p-2 border rounded">
                                    <Checkbox
                                        checked={selectedAttributes.includes(attr._id)}
                                        onChange={(value, checked) => handleCheckboxChange(attr._id, checked)}
                                    >
                                        <span className="font-weight-bold">{attr.name}</span>
                                        {attr.attributeType && attr.attributeType !== "user_input" && attr.attributeType !== "user.input" && (
                                            <>
                                                <br />
                                                <small className="text-muted">{attr.attributeType}</small>
                                            </>
                                        )}
                                    </Checkbox>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            );
        });

        const ungroupedAttrs = filteredAttributes.filter(a => {
            const attrGroupId = a.groupingId?._id || a.groupingId;
            return !attrGroupId;
        });

        let ungroupedRender = null;
        if (ungroupedAttrs.length > 0) {
            ungroupedRender = (
                <div key="ungrouped" className="mb-4 border rounded p-3">
                    <h5 className="mb-3 font-weight-bold ml-4">Other Attributes</h5>
                    <div className="row m-0">
                        {ungroupedAttrs.map(attr => (
                            <div key={attr._id} className="col-md-6 mb-2">
                                <div className="d-flex align-items-center gap-2 p-2 border rounded">
                                    <Checkbox
                                        checked={selectedAttributes.includes(attr._id)}
                                        onChange={(value, checked) => handleCheckboxChange(attr._id, checked)}
                                    >
                                        <span className="font-weight-bold">{attr.name}</span>
                                        {attr.attributeType && attr.attributeType !== "user_input" && attr.attributeType !== "user.input" && (
                                            <>
                                                <br />
                                                <small className="text-muted">{attr.attributeType}</small>
                                            </>
                                        )}
                                    </Checkbox>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            );
        }

        const hasRenderedItems = groupsToRender.some(g => g !== null) || ungroupedRender !== null;

        if (!hasRenderedItems) {
            return (
                <div className="text-center py-4 text-muted">
                    No attributes found matching your criteria.
                </div>
            );
        }

        return (
            <>
                {groupsToRender}
                {ungroupedRender}
            </>
        );
    };

    return (
        <Modal open={open} onClose={onClose} size="lg">
            <Modal.Header>
                <Modal.Title>Assign Attributes to {preSubCategory?.title}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="mb-4">
                    <Input 
                        placeholder="Search attributes..." 
                        value={searchQuery} 
                        onChange={(value) => setSearchQuery(value)} 
                    />
                </div>
                {renderGroupings()}
            </Modal.Body>
            <Modal.Footer>
                <Button onClick={handleSave} appearance="primary" disabled={saving || loading}>
                    {saving ? "Saving..." : "Save Assignments"}
                </Button>
                <Button onClick={onClose} appearance="subtle">
                    Cancel
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default AssignAttributesModal;
